import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { projectsAPI, dashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { mapBackendProjectsToFrontend } from '../utils/dataMappers';

/* ── Animated counter hook ───────────────────────────────────────────────── */
function useCountUp(target: number | null, duration = 1400) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>(0);
  useEffect(() => {
    if (target === null) return;
    let start: number | null = null;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(ease * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return value;
}

/* ── Stat counter cell ───────────────────────────────────────────────────── */
function StatCounter({ label, value, sub, accent = false }: {
  label: string; value: string; sub: string; accent?: boolean;
}) {
  return (
    <div className={`border-l-[3px] pl-5 ${accent ? 'border-teal-400' : 'border-primary-500'}`}>
      <div className="text-2xl lg:text-3xl font-light text-carbon-100 tabular-nums mb-0.5 animate-count-up">
        {value}
      </div>
      <div className="text-xs font-semibold text-carbon-100 uppercase tracking-widest mb-0.5">{label}</div>
      <div className="text-xs text-carbon-40">{sub}</div>
    </div>
  );
}

/* ── How It Works step ───────────────────────────────────────────────────── */
function HowStep({ n, title, desc }: { n: string; title: string; desc: string }) {
  return (
    <div className="flex gap-5">
      <div className="shrink-0 w-10 h-10 bg-primary-500 flex items-center justify-center text-white font-semibold text-sm">{n}</div>
      <div>
        <h4 className="text-sm font-semibold text-carbon-100 mb-1">{title}</h4>
        <p className="text-xs text-carbon-50 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

/* ── Trust badge ─────────────────────────────────────────────────────────── */
function TrustBadge({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <svg className="w-3.5 h-3.5 text-blue-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      <span className="text-[11px] text-blue-200/60 uppercase tracking-[0.12em] font-medium">{label}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HOME PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
export default function Home() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [featuredProjects, setFeaturedProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [publicStats, setPublicStats] = useState<{
    activeProjects: number;
  } | null>(null);

  const projects = useCountUp(publicStats?.activeProjects ?? null);

  useEffect(() => {
    document.title = 'Rahmani Foundation — Empowering Lives Through Collective Action';
    projectsAPI.getFeatured()
      .then(res => {
        const data = res.data?.projects || res.data?.content || res.data || [];
        setFeaturedProjects(mapBackendProjectsToFrontend(data).slice(0, 3));
      })
      .catch(() => setFeaturedProjects([]))
      .finally(() => setLoading(false));
    dashboardAPI.getPublicStats()
      .then(res => setPublicStats(res.data?.stats || null))
      .catch(() => {});
  }, []);

  if (isAuthenticated && !authLoading) return <Navigate to="/dashboard" replace />;

  const HERO_IMG = 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=1400&auto=format&fit=crop';
  const TEAM_IMG = 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=1400&auto=format&fit=crop';
  const IMPACT_IMG = 'https://images.unsplash.com/photo-1542884748-2b87b36c6b90?q=80&w=1000&auto=format&fit=crop';

  return (
    <div className="min-h-screen bg-white">

      {/* ═══ HERO ══════════════════════════════════════════════════════════ */}
      <section style={{ background: 'linear-gradient(160deg, #060d1f 0%, #0a1e45 55%, #0d3070 100%)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">

            {/* Left — copy */}
            <div className="animate-fade-up">
              {/* Live indicator */}
              <div className="flex items-center gap-2 mb-5">
                <span className="w-2 h-2 bg-teal-300 animate-pulse-dot" style={{ borderRadius: 0 }} />
                <span className="text-[11px] text-teal-300/80 uppercase tracking-[0.14em] font-medium">
                  Official Humanitarian Platform
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-light text-white mb-6 leading-[1.1]">
                Empowering Lives<br />
                Through{' '}
                <span className="font-semibold" style={{ color: '#60a5fa' }}>Collective&nbsp;Action</span>
              </h1>

              <p className="text-base text-blue-200/65 mb-9 leading-relaxed max-w-lg">
                Join thousands supporting verified humanitarian initiatives — with complete transparency,
                secure payments and instant 80G tax receipts.
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                <Link to="/projects" className="btn btn-primary btn-lg">
                  Explore Projects
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="/donate"
                  className="btn btn-lg"
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.22)', color: '#fff' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  Donate Now
                </Link>
              </div>

              {/* Trust row */}
              <div className="flex flex-wrap gap-6 pt-7 border-t border-white/10">
                {['Transparent', 'Secure', 'Impactful', '80G Certified'].map(l => (
                  <TrustBadge key={l} label={l} />
                ))}
              </div>
            </div>

            {/* Right — image card + floating stat chips */}
            <div className="relative hidden lg:block animate-scale-in" style={{ animationDelay: '120ms' }}>
              {/* Decorative accent line */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 pointer-events-none"
                style={{ borderColor: 'rgba(96,165,250,.3)' }} />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 pointer-events-none"
                style={{ borderColor: 'rgba(96,165,250,.3)' }} />

              <div className="overflow-hidden border border-white/10">
                <img
                  src={HERO_IMG}
                  alt="Humanitarian impact"
                  className="w-full h-[400px] object-cover opacity-80"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,13,31,.8) 0%, transparent 55%)' }} />
              </div>

              {/* Floating stat chip */}
              <div className="absolute -bottom-6 left-6 right-6 grid grid-cols-1 gap-2">
                {[
                  { label: 'Active Projects', value: publicStats ? `${projects}+` : '—' },
                ].map(s => (
                  <div key={s.label} className="text-center py-3.5 px-2"
                    style={{ background: 'rgba(15,98,254,0.92)', border: '1px solid rgba(255,255,255,.12)', backdropFilter: 'blur(12px)' }}>
                    <p className="text-lg font-semibold text-white leading-none">{s.value}</p>
                    <p className="text-[10px] text-blue-200/70 mt-0.5 uppercase tracking-wide">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile stats strip */}
        <div className="lg:hidden border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 divide-x divide-white/10">
            {[
              { label: 'Projects',     value: publicStats ? `${projects}+` : '—' },
              { label: 'Transparency', value: '100%' },
            ].map(s => (
              <div key={s.label} className="py-5 text-center">
                <p className="text-xl font-semibold text-white">{s.value}</p>
                <p className="text-[10px] text-blue-200/50 mt-0.5 uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ STATS ROW ═════════════════════════════════════════════════════ */}
      <section className="bg-white border-b border-carbon-20 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-6 lg:gap-8">
            <StatCounter
              label="Active Projects"
              value={publicStats ? `${projects}+` : '—'}
              sub="Running right now"
            />
            <StatCounter
              label="Transparency"
              value="100%"
              sub="Verified audit trail"
              accent
            />
          </div>
        </div>
      </section>

      {/* ═══ FEATURED PROJECTS ═════════════════════════════════════════════ */}
      <section className="py-16 lg:py-20 bg-carbon-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <span className="eyebrow mb-2 block">Urgent Needs</span>
              <h2 className="text-2xl lg:text-3xl font-semibold text-carbon-100">Current Welfare Missions</h2>
            </div>
            <Link to="/projects" className="btn btn-outline btn-sm gap-1.5 self-start">
              View all projects
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="skeleton h-80" />)}
            </div>
          ) : featuredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProjects.map((project, idx) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="card card-lift group flex flex-col bg-white"
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div className="relative h-48 overflow-hidden bg-carbon-10 shrink-0">
                    <img
                      src={project.imageUrl || HERO_IMG}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="badge-blue mb-3 self-start text-[11px]">{project.category || 'Humanitarian'}</span>
                    <h3 className="text-sm font-semibold text-carbon-100 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                      {project.title}
                    </h3>
                    <p className="text-xs text-carbon-50 mb-4 line-clamp-2 leading-relaxed">{project.description}</p>
                    <div className="mt-auto">
                      <div className="btn btn-primary w-full text-xs">Contribute now →</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="card border-dashed text-center py-20">
              <p className="text-carbon-40 text-sm">No active missions right now. Check back soon.</p>
              <Link to="/projects" className="btn btn-primary mt-4">Browse All Projects</Link>
            </div>
          )}
        </div>
      </section>

      {/* ═══ WHY RAHMANI — feature grid ════════════════════════════════════ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-xl mb-12">
            <span className="eyebrow mb-2 block">Why Choose Us</span>
            <h2 className="text-2xl lg:text-3xl font-semibold text-carbon-100">
              Built on Trust, Backed by Accountability
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-carbon-20">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
                title: 'Fully Verified',
                desc: 'Every donation tracked, every rupee accounted for — complete donor-to-impact visibility with real-time receipts.',
                accent: 'border-primary-500',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ),
                title: '80G Tax Receipts',
                desc: 'Auto-generated 80G receipts sent instantly — complete documentation for stress-free tax compliance.',
                accent: 'border-teal-400',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                ),
                title: 'Bank-Grade Security',
                desc: 'TLS 1.3 encryption, Easebuzz-certified payments, and GDPR-aligned data handling at every step.',
                accent: 'border-accent-500',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ),
                title: 'Ambassador Network',
                desc: 'A verified network of certified ambassadors bridging local needs to global donors — endorsed by Rahmani Foundation.',
                accent: 'border-primary-500',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: 'Real-Time Reporting',
                desc: 'Live dashboards for donors, ambassadors and admins — always know where your contribution stands.',
                accent: 'border-teal-400',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                ),
                title: 'Recurring Giving',
                desc: 'Set up automated monthly contributions to sustain long-term welfare missions without manual effort.',
                accent: 'border-accent-500',
              },
            ].map((item, i) => {
              const borderRight  = (i % 3 !== 2) ? 'lg:border-r border-carbon-20' : '';
              const borderBottom = i < 3          ? 'border-b border-carbon-20'    : '';
              return (
                <div key={item.title} className={`p-8 flex flex-col gap-4 ${borderRight} ${borderBottom} border-b md:border-b-0`}>
                  <div className={`w-11 h-11 border border-carbon-20 flex items-center justify-center text-primary-500 shrink-0 border-t-2 ${item.accent}`}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-carbon-100 mb-1.5">{item.title}</h3>
                    <p className="text-sm text-carbon-50 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ══════════════════════════════════════════════════ */}
      <section className="py-16 lg:py-20 bg-carbon-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">

            {/* Image side */}
            <div className="relative order-2 lg:order-1 overflow-hidden border border-carbon-20">
              <img src={IMPACT_IMG} alt="Impact" className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(22,22,22,.7) 0%, transparent 55%)' }} />
              {/* Floating quote */}
              <div className="absolute bottom-6 left-6 right-6 p-5 border border-white/15"
                style={{ background: 'rgba(22,22,22,.88)', backdropFilter: 'blur(8px)' }}>
                <p className="text-white font-semibold text-base leading-snug">
                  "Every rupee reaches the right hands."
                </p>
                <p className="text-white/50 text-[11px] uppercase tracking-widest mt-1.5">
                  Rahmani Foundation — Munger, Bihar
                </p>
              </div>
            </div>

            {/* Steps side */}
            <div className="order-1 lg:order-2">
              <span className="eyebrow mb-3 block">Simple Process</span>
              <h2 className="text-2xl lg:text-[2rem] font-semibold text-carbon-100 mb-8 leading-tight">
                How Giving Works<br />
                <span className="text-primary-500">in Four Steps</span>
              </h2>
              <div className="space-y-6">
                <HowStep n="01" title="Create Your Account"
                  desc="Sign up with Google in seconds. Your KYC details are securely stored for instant receipt generation." />
                <HowStep n="02" title="Choose a Mission"
                  desc="Browse verified welfare projects — education, healthcare, relief, infrastructure — and select what matters most to you." />
                <HowStep n="03" title="Donate Securely"
                  desc="Pay via UPI, card or net banking through Easebuzz. 100% encrypted, PCI-DSS compliant." />
                <HowStep n="04" title="Receive 80G Receipt"
                  desc="An official 80G tax receipt is emailed instantly. Track your impact from your donor dashboard." />
              </div>
              <Link to="/login" className="btn btn-primary btn-lg mt-10">
                Get Started Free
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ AMBASSADOR ════════════════════════════════════════════════════ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="eyebrow mb-3 block">Official Network</span>
              <h2 className="text-2xl lg:text-[2rem] font-semibold text-carbon-100 mb-5 leading-tight">
                Become a Foundation<br />
                <span className="text-primary-500">Official Ambassador</span>
              </h2>
              <p className="text-carbon-60 text-sm leading-relaxed mb-8 max-w-sm">
                Join our exclusive network of authorized ambassadors — the critical bridge between
                local needs and global support.
              </p>

              <div className="space-y-5 mb-8">
                {[
                  { title: 'Official Certification', desc: 'Receive digital credentials as an authorized representative of Rahmani Foundation.' },
                  { title: 'Community Leadership',   desc: 'Lead humanitarian initiatives and verify local impact in your region.' },
                  { title: 'Verified Status',         desc: 'Build donor trust with a secure, foundation-backed ambassador identity.' },
                ].map((item, i) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-7 h-7 bg-primary-500 flex items-center justify-center shrink-0 text-white text-xs font-semibold mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-carbon-100 mb-0.5">{item.title}</h4>
                      <p className="text-xs text-carbon-50 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/profile" className="btn btn-primary btn-lg">
                Apply for Authorization
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>

            <div className="relative overflow-hidden border border-carbon-20">
              <img src={TEAM_IMG} alt="Ambassador Network" className="w-full h-[400px] object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(22,22,22,.75) 0%, transparent 55%)' }} />
              {/* Floating quote card */}
              <div className="absolute bottom-6 left-6 right-6 p-5 border border-white/15"
                style={{ background: 'rgba(22,22,22,.85)', backdropFilter: 'blur(8px)' }}>
                <p className="text-white font-semibold text-lg leading-snug">"The Bridge to Hope"</p>
                <p className="text-white/50 text-[11px] uppercase tracking-widest mt-1">
                  Official Ambassador Circle
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ IMPACT NUMBERS BAND ══════════════════════════════════════════ */}
      <section style={{ background: 'linear-gradient(135deg, #001141 0%, #002d9c 100%)' }} className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '20+',  label: 'Years of Service',       sub: 'Founded 2003' },
              { value: '5K+',  label: 'Beneficiaries Served',   sub: 'Direct impact' },
              { value: '₹2Cr+',label: 'Funds Mobilised',        sub: 'Across all channels' },
              { value: '12+',  label: 'States Covered',         sub: 'All-India reach' },
            ].map(s => (
              <div key={s.label} className="flex flex-col items-center">
                <div className="text-3xl lg:text-4xl font-light text-white mb-1 tabular-nums">{s.value}</div>
                <div className="text-xs font-semibold text-blue-200/80 uppercase tracking-widest mb-0.5">{s.label}</div>
                <div className="text-[11px] text-blue-300/40">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-carbon-100 px-8 lg:px-16 py-14 lg:py-20 text-center border border-carbon-90 relative overflow-hidden">
            {/* Radial glow */}
            <div className="absolute inset-0 pointer-events-none opacity-25"
              style={{ background: 'radial-gradient(ellipse at 50% 70%, #1d6fce 0%, transparent 65%)' }} />
            {/* Grid texture */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
              style={{ backgroundImage: 'repeating-linear-gradient(0deg, #fff 0px, #fff 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, #fff 0px, #fff 1px, transparent 1px, transparent 40px)' }} />

            <div className="relative z-10">
              <span className="eyebrow mb-4 block" style={{ color: '#60a5fa' }}>Take Action Today</span>
              <h2 className="text-3xl lg:text-4xl font-light text-white mb-5 max-w-2xl mx-auto leading-[1.15]">
                Ready to{' '}
                <span className="font-semibold">Shape the Future?</span>
              </h2>
              <p className="text-white/50 text-sm mb-10 max-w-lg mx-auto leading-relaxed">
                Join our circle of donors supporting Rahmani Foundation's global welfare initiatives
                and community transformation — starting with a single contribution.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/projects" className="btn btn-primary btn-lg">
                  Begin Contributing
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
                <Link
                  to="/login"
                  className="btn btn-lg"
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
